/**
 * PeriodAxis — одна рейка дат под подневным графиком.
 *
 * Раньше под календарём стояли две строки: названия месяцев и, отдельной
 * строкой, границы периода. Две рейки под одним графиком читатель принимает
 * за две разные шкалы и ищет между ними связь, которой нет.
 *
 * Здесь всё на одной строке, и приоритет у краёв: границы периода названы
 * точными датами всегда — по ним отвечают на вопрос «за что отчёт». Месяцы
 * внутри — справка о том, где проходят их стыки; они и уходят первыми, когда
 * места не хватает.
 *
 * Порядок вытеснения именно такой, а не «сначала середина, потом края»,
 * потому что вопросы разной цены: не увидев апреля, читатель по-прежнему
 * знает период; не увидев границ — не знает ничего.
 *
 * @format
 */

import {useLayoutEffect, useMemo, useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {dayMonth, longDate, monthName, parseIsoDate} from '../helpers';
import type {IsoDate} from '../types';

export interface PeriodAxisProps {
  /** Даты ряда, от старой к новой. Ровно те, над которыми стоят столбики. */
  dates: IsoDate[];
  /**
   * Отступы слева и справа, если поле построения уже контейнера. У
   * рукодельного календаря их нет, у графика на Recharts слева стоит шкала
   * значений, справа — запас под последнюю подпись оси.
   */
  insetLeft?: number;
  insetRight?: number;
}

/**
 * Запас вокруг подписи месяца.
 *
 * Без него месяц встаёт вплотную к границе периода, и две даты читаются как
 * одна разорванная. Столько же требуется между соседними месяцами.
 */
const GAP = 12;

const Rail = styled(Box)({
  ...text('label.s'),
  color: surfaceVar('onSurfaceMuted'),
  position: 'relative',
  paddingTop: spacingPx('xxs'),
  // Высота держится всегда, даже когда все месяцы спрятаны: рейка не должна
  // менять высоту страницы от ширины окна.
  minHeight: spacingPx('xxs') + 16,
});

/** Месяцы стоят по своим отрезкам, поэтому это ряд с долями, а не текст. */
const Months = styled(Box)({
  display: 'flex',
  position: 'absolute',
  top: spacingPx('xxs'),
  left: 0,
  right: 0,
});

const MonthCell = styled(Box)({
  textAlign: 'center',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

/**
 * Граница периода. Заметнее месяцев: это ответ на вопрос «за что отчёт», а
 * месяц — только пометка о стыке.
 */
const Edge = styled(Box)({
  color: surfaceVar('onSurfaceLight'),
  position: 'absolute',
  top: spacingPx('xxs'),
  whiteSpace: 'nowrap',
});

interface MonthSpan {
  name: string;
  /** Сколько дней ряда пришлось на этот месяц. */
  count: number;
}

function monthSpans(dates: IsoDate[]): MonthSpan[] {
  const spans: MonthSpan[] = [];
  dates.forEach(date => {
    const name = monthName(date);
    const last = spans.length > 0 ? spans[spans.length - 1] : null;
    if (last && last.name === name) {
      last.count += 1;
      return;
    }
    spans.push({name, count: 1});
  });
  return spans;
}

/**
 * Границы периода словами. Год называется только тогда, когда период его
 * пересекает: иначе он уже стоит в шапке отчёта.
 */
function edgeLabels(dates: IsoDate[]) {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return null;
  const a = parseIsoDate(first);
  const b = parseIsoDate(last);
  return a != null && b != null && a.year === b.year
    ? {from: dayMonth(first), to: dayMonth(last)}
    : {from: longDate(first), to: longDate(last)};
}

export function PeriodAxis({dates, insetLeft = 0, insetRight = 0}: PeriodAxisProps) {
  const spans = useMemo(() => monthSpans(dates), [dates]);
  const edges = useMemo(() => edgeLabels(dates), [dates]);

  const railRef = useRef<HTMLDivElement>(null);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);
  const monthRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hidden, setHidden] = useState<boolean[]>([]);

  /**
   * Что поместилось — решает измерение, а не догадка о числе знаков.
   *
   * Меряем по факту: подпись прячется, если не влезает в свой отрезок или
   * налезает на границу периода. Считать наперёд по средней ширине буквы
   * нельзя — «май» и «сентябрь» отличаются втрое, а шрифт может подмениться
   * на любой машине.
   */
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    const measure = () => {
      const fromBox = fromRef.current?.getBoundingClientRect();
      const toBox = toRef.current?.getBoundingClientRect();

      const next = monthRefs.current.map(cell => {
        if (!cell) return true;
        const label = cell.firstElementChild;
        if (!label) return true;

        // Нулевая ширина — это «ещё не разложилось», а не «не поместилось».
        // Прятать по такому замеру нельзя: раскладка потом наступит, а
        // спрятанная подпись сама собой не вернётся.
        if (cell.clientWidth === 0) return false;

        // Ширина по факту отрисовки, а не `scrollWidth`: подпись строчная и
        // не переносится, поэтому её бокс и есть натуральная ширина слова.
        const box = label.getBoundingClientRect();

        // Подпись шире своего отрезка — месяц слишком короткий, чтобы его
        // называть. Скрываем целиком: обрезанное слово хуже отсутствующего.
        if (box.width + GAP > cell.clientWidth) return true;

        const clashes = (edge?: DOMRect) =>
          edge != null && box.left < edge.right + GAP && box.right + GAP > edge.left;

        return clashes(fromBox) || clashes(toBox);
      });

      setHidden(prev =>
        prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next,
      );
    };

    measure();

    // Наблюдаем не только рейку, но и сами подписи. Ширина рейки может не
    // меняться вовсе, а подписи — менять: подмена шрифта на загруженный
    // переставляет их на несколько пикселей, и замер, сделанный на
    // подстановочном шрифте, остался бы навсегда.
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    [...monthRefs.current, fromRef.current, toRef.current].forEach(node => {
      if (node) observer.observe(node);
    });

    // Готовность шрифтов — отдельный сигнал: если ширина подписи от подмены
    // не изменилась, наблюдатель не сработает, а положение соседей всё равно
    // могло поехать.
    //
    // Явная проверка, а не `?.`: по типам `document.fonts` есть всегда, и
    // `eslint --fix` снял бы опциональную цепочку как лишнюю. В jsdom этого
    // объекта нет, и без проверки падает весь этаж.
    const {fonts} = document as Omit<Document, 'fonts'> & {fonts?: FontFaceSet};
    let alive = true;
    if (fonts) {
      void fonts.ready.then(() => {
        if (alive) measure();
      });
    }

    return () => {
      alive = false;
      observer.disconnect();
    };
  }, [spans, edges]);

  if (!edges) return null;

  return (
    <Rail ref={railRef} style={{paddingLeft: insetLeft, paddingRight: insetRight}}>
      <Months style={{left: insetLeft, right: insetRight}}>
        {spans.map((span, index) => (
          <MonthCell
            key={span.name}
            ref={(node: HTMLDivElement | null) => {
              monthRefs.current[index] = node;
            }}
            style={{flex: span.count, visibility: hidden[index] ? 'hidden' : 'visible'}}
          >
            {/* Внутренний span нужен измерению: у ячейки ширина задана долей
                отрезка, а натуральную ширину подписи знает только она сама. */}
            <Box component='span'>{span.name}</Box>
          </MonthCell>
        ))}
      </Months>

      <Edge ref={fromRef} style={{left: insetLeft}}>
        {edges.from}
      </Edge>
      <Edge ref={toRef} style={{right: insetRight}}>
        {edges.to}
      </Edge>
    </Rail>
  );
}
